import React, { useState, useEffect } from 'react';
import { supabase, auth, syncSession } from '@/lib/supabase';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { IconLock, IconMail, IconArrowLeft } from '@tabler/icons-react';
import { safeGetItem } from '@/utils/safeLocalStorage';

/**
 * Password Reset Flow
 * Handles both:
 * 1. Requesting password reset email
 * 2. Setting new password after clicking reset link
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [mode, setMode] = useState('checking'); // Start with checking - we need to verify session state

  // Check session state on mount - Supabase may have already processed recovery tokens
  useEffect(() => {
    const checkSessionAndMode = async () => {
      const hash = window.location.hash;
      const type = searchParams.get('type');
      const code = searchParams.get('code'); // PKCE code from Supabase
      const tokenHash = searchParams.get('token_hash'); // Direct token from our edge function

      console.log('[Reset Password] Checking session state...', {
        hasHash: !!hash,
        hashPreview: hash?.substring(0, 50),
        type,
        hasCode: !!code,
        hasTokenHash: !!tokenHash
      });

      // PRIMARY PATH: token_hash from our edge function (bypasses PKCE entirely)
      // Our send-password-reset-email edge function builds the URL with hashed_token
      // so we can verify it directly without needing a code_verifier.
      //
      // RACE CONDITION: detectSessionInUrl (in supabase config) may auto-consume
      // the token before we get here. If verifyOtp fails, check if a session was
      // already established by the auto-detection.
      if (tokenHash && type === 'recovery') {
        console.log('[Reset Password] Token hash found, verifying via OTP...');
        setLoading(true);

        try {
          const { data, error } = await auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery',
          });

          if (error) {
            console.warn('[Reset Password] verifyOtp failed (token may have been auto-consumed):', error.message);
            // detectSessionInUrl may have already consumed the token and established a session
            const { data: { session } } = await auth.getSession();
            if (session) {
              console.log('[Reset Password] Session already established via auto-detection, proceeding to reset');
              window.history.replaceState(null, '', window.location.pathname);
              setMode('reset');
            } else {
              console.error('[Reset Password] No session found either — link is truly expired');
              setMessage({
                type: 'error',
                text: 'Invalid or expired reset link. Please request a new one.'
              });
              setMode('request');
            }
          } else {
            console.log('[Reset Password] Token verified, session established');
            window.history.replaceState(null, '', window.location.pathname);
            setMode('reset');
          }
        } catch (err) {
          console.error('[Reset Password] Error verifying token:', err);
          // Same fallback — check for auto-established session
          const { data: { session } } = await auth.getSession();
          if (session) {
            console.log('[Reset Password] Session found despite error, proceeding');
            window.history.replaceState(null, '', window.location.pathname);
            setMode('reset');
          } else {
            setMessage({ type: 'error', text: 'Failed to process reset link. Please request a new one.' });
            setMode('request');
          }
        } finally {
          setLoading(false);
        }
        return;
      }

      // FALLBACK: PKCE code exchange (for legacy links or Supabase-generated emails)
      if (code && type === 'recovery') {
        console.log('[Reset Password] PKCE recovery code found, exchanging for session...');
        setLoading(true);

        try {
          const codeVerifier = safeGetItem('supabase.auth.code_verifier') ||
                              safeGetItem('odv-supabase-auth-code-verifier');

          if (!codeVerifier) {
            console.warn('[Reset Password] No code verifier found - link may be from different browser/session');
            setMessage({
              type: 'error',
              text: 'Reset link expired or opened in wrong browser. Please request a new reset link from the same browser.'
            });
            setMode('request');
            setLoading(false);
            return;
          }

          const { data, error } = await auth.exchangeCodeForSession(code);

          if (error) {
            console.error('[Reset Password] Failed to exchange code:', error);
            if (error.message?.includes('code verifier')) {
              setMessage({
                type: 'error',
                text: 'Reset link expired or opened in wrong browser. Please request a new reset link.'
              });
            } else {
              setMessage({
                type: 'error',
                text: 'Invalid or expired reset link. Please request a new one.'
              });
            }
            setMode('request');
          } else {
            console.log('[Reset Password] Code exchanged successfully, session established');
            window.history.replaceState(null, '', window.location.pathname);
            setMode('reset');
          }
        } catch (err) {
          console.error('[Reset Password] Error exchanging code:', err);
          setMessage({ type: 'error', text: 'Failed to process reset link. Please request a new one.' });
          setMode('request');
        } finally {
          setLoading(false);
        }
        return;
      }

      // Handle OAuth callback codes that ended up here by mistake
      if (code && !type) {
        console.log('[Reset Password] OAuth callback code detected (not a password reset) - redirecting to /auth');
        navigate(`/auth?code=${encodeURIComponent(code)}`, { replace: true });
        return;
      }

      // Check if we have an active session (Supabase may have auto-processed tokens)
      // Use auth client directly - it's not blocked like the main supabase client
      const { data: { session }, error: sessionError } = await auth.getSession();

      if (sessionError) {
        console.error('[Reset Password] Error getting session:', sessionError);
      }

      console.log('[Reset Password] Current session:', session ? 'exists' : 'none');

      // If URL still has tokens in hash, try to process them manually
      if (hash && hash.includes('access_token')) {
        console.log('[Reset Password] Recovery tokens in URL hash, processing...');
        setLoading(true);

        try {
          const hashParams = new URLSearchParams(hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          const tokenType = hashParams.get('type');

          console.log('[Reset Password] Token type:', tokenType);

          if (accessToken && refreshToken) {
            // Use auth client directly for session management
            const { data, error } = await auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });

            if (error) {
              console.error('[Reset Password] Failed to set session:', error);
              setMessage({
                type: 'error',
                text: 'Invalid or expired reset link. Please request a new one.'
              });
              setMode('request');
            } else {
              console.log('[Reset Password] Session established successfully');
              window.history.replaceState(null, '', window.location.pathname);
              setMode('reset');
            }
          } else {
            setMessage({ type: 'error', text: 'Invalid reset link. Please request a new one.' });
            setMode('request');
          }
        } catch (err) {
          console.error('[Reset Password] Error processing token:', err);
          setMessage({ type: 'error', text: 'Failed to process reset link. Please request a new one.' });
          setMode('request');
        } finally {
          setLoading(false);
        }
      }
      // If we have a session (Supabase already processed the tokens), go to reset mode
      else if (session) {
        console.log('[Reset Password] Session exists, switching to reset mode');
        setMode('reset');
      }
      // URL has type=recovery but no hash and no session
      else if (type === 'recovery') {
        console.log('[Reset Password] Recovery type but no tokens/session');
        setMessage({
          type: 'error',
          text: 'Reset link incomplete. Please click the link directly from your email, or request a new one.'
        });
        setMode('request');
      }
      // No tokens, no session - show request form
      else {
        console.log('[Reset Password] No session, showing request form');
        setMode('request');
      }
    };

    checkSessionAndMode();
  }, [searchParams]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Use custom Edge Function for beautiful branded email instead of Supabase default
      const { data, error } = await supabase.functions.invoke('send-password-reset-email', {
        body: { email: email.trim().toLowerCase() }
      });

      if (error) {
        // Extract actual error message from edge function response
        let errorMsg = 'Failed to send reset email';
        try {
          if (error.context && typeof error.context.json === 'function') {
            const errorBody = await error.context.json();
            errorMsg = errorBody?.error || errorBody?.message || errorMsg;
          } else if (error.message && error.message !== 'Edge Function returned a non-2xx status code') {
            errorMsg = error.message;
          }
        } catch (_) {
          // Fall back to generic message
        }
        console.error('[Reset Password] Edge function error:', errorMsg);
        throw new Error(errorMsg);
      }
      if (!data?.success) throw new Error(data?.error || 'Failed to send reset email');

      if (data.sent === false) {
        // No auth account found — show helpful guidance without revealing account existence
        setMessage({
          type: 'info',
          text: 'If an account exists with this email, a reset link will be sent. Don\'t see it? Check your spam folder, or try signing in with Google or Discord instead.',
        });
      } else {
        setMessage({
          type: 'success',
          text: 'Check your email! We sent you a reset link. Check spam if you don\'t see it within a few minutes.',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Failed to send reset email. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

    const handleUpdatePassword = async (e) => {
      e.preventDefault();
      setLoading(true);
      setMessage({ type: '', text: '' });

      // Validate passwords
      if (password.length < 8) {
        setMessage({
          type: 'error',
          text: 'Password must be at least 8 characters long.',
        });
        setLoading(false);
        return;
      }

      if (password !== confirmPassword) {
        setMessage({
          type: 'error',
          text: 'Passwords do not match.',
        });
        setLoading(false);
        return;
      }

      try {
        const { data: sessionData, error: sessionError } = await auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        const activeSession = sessionData?.session;

        if (!activeSession) {
          setMessage({
            type: 'error',
            text: 'Auth session missing. Please reopen the reset link from your email.',
          });
          setLoading(false);
          return;
        }

        const { error } = await auth.updateUser({
          password: password,
        });

        if (error) throw error;

        try {
          await syncSession(activeSession);
        } catch (syncError) {
          console.warn('[Reset Password] Failed to sync session after password reset:', syncError);
        }

        setMessage({
          type: 'success',
          text: '✅ Password updated successfully! Redirecting to login...',
        });

        setTimeout(() => {
          navigate('/auth', { replace: true });
        }, 2000);
      } catch (error) {
        setMessage({
          type: 'error',
          text: error.message || 'Failed to update password. Please try again.',
        });
        setLoading(false);
      }
    };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Main Card */}
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl overflow-hidden border border-white/10">
          {/* Header Section */}
          <div className="bg-gradient-to-br from-black via-gray-900 to-black p-8 text-center border-b border-white/10">
            <div className="mx-auto w-16 h-16 bg-[#AEA36D]/20 rounded-full flex items-center justify-center mb-4">
              <IconLock className="w-8 h-8 text-[#AEA36D]" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {mode === 'checking' ? 'Please Wait...' : mode === 'request' ? 'Reset Password' : 'Set New Password'}
            </h1>
            <p className="text-gray-400 text-sm">
              {mode === 'checking'
                ? 'Verifying your reset link...'
                : mode === 'request'
                ? "Enter your email and we'll send you a reset link"
                : 'Enter your new password below'}
            </p>
          </div>

          {/* Form Section */}
          <div className="p-8">
            {/* Message Alert */}
            {message.text && (
              <Alert
                className={`mb-6 ${
                  message.type === 'success'
                    ? 'bg-green-500/20 border-green-500/50 text-green-100'
                    : message.type === 'info'
                    ? 'bg-blue-500/20 border-blue-500/50 text-blue-100'
                    : 'bg-red-500/20 border-red-500/50 text-red-100'
                }`}
              >
                <AlertDescription>{message.text}</AlertDescription>
              </Alert>
            )}

            {/* Checking/Processing State */}
            {mode === 'checking' && (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="w-12 h-12 border-4 border-[#AEA36D]/30 border-t-[#AEA36D] rounded-full animate-spin mb-4"></div>
                <p className="text-gray-400">Verifying your reset link...</p>
              </div>
            )}

            {/* Request Reset Form */}
            {mode === 'request' && (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#AEA36D] mb-2">
                    Email Address
                  </label>
                  <div className="rounded-lg bg-gradient-to-r from-[#AEA36D] via-white to-[#AEA36D] p-[2px]">
                    <div className="relative">
                      <IconMail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@opendoorvalet.com"
                        required
                        className="w-full pl-10 bg-black/60 border-0 text-white placeholder:text-gray-400 focus:ring-2 focus:ring-[#AEA36D]/50 rounded-lg"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-[#AEA36D] hover:bg-[#9A9360] text-black font-medium py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                {/* OAuth hint */}
                <div className="text-center text-xs text-gray-500 py-2">
                  Signed up with Google or Discord?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/auth')}
                    className="text-[#AEA36D] hover:underline"
                  >
                    Sign in that way instead
                  </button>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/auth')}
                  className="w-full text-gray-400 hover:text-white"
                >
                  <IconArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </form>
            )}

            {/* Reset Password Form */}
            {mode === 'reset' && (
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#AEA36D] mb-2">
                    New Password
                  </label>
                  <div className="rounded-lg bg-gradient-to-r from-[#AEA36D] via-white to-[#AEA36D] p-[2px]">
                    <div className="relative">
                      <IconLock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Minimum 8 characters"
                        required
                        minLength={8}
                        className="w-full pl-10 bg-black/60 border-0 text-white placeholder:text-gray-400 focus:ring-2 focus:ring-[#AEA36D]/50 rounded-lg"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Must be at least 8 characters long
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#AEA36D] mb-2">
                    Confirm Password
                  </label>
                  <div className="rounded-lg bg-gradient-to-r from-[#AEA36D] via-white to-[#AEA36D] p-[2px]">
                    <div className="relative">
                      <IconLock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter your password"
                        required
                        minLength={8}
                        className="w-full pl-10 bg-black/60 border-0 text-white placeholder:text-gray-400 focus:ring-2 focus:ring-[#AEA36D]/50 rounded-lg"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                  className="w-full bg-[#AEA36D] hover:bg-[#9A9360] text-black font-medium py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-gray-400 text-sm">
          <p className="italic">Hospitality, Driven.</p>
          <p className="mt-2">© 2025 Open Door Valet. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
